/**
 * <legal-content>: legal page body (sections/legal-main.liquid). Progressive enhancement only: adds a
 * « Copier le modèle » button to each withdrawal template (`.lg-form`) pasted in the page content, and
 * copies it as plain text. Labels come from data-copy / data-copied (translated in Liquid).
 */

const COPY_ICON =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/></svg>';

/**
 * Plain-text version of a template block: one line per paragraph (line breaks kept), and one
 * « Label : » line per blank to fill (`.lg-lines > *`). The copy button itself is left out.
 *
 * @param {Element} form
 * @returns {string}
 */
export function templateText(form) {
  /** @type {string[]} */
  const lines = [];
  for (const child of form.children) {
    if (child.matches('.lg-copy')) continue;
    if (child.matches('.lg-lines')) {
      lines.push([...child.children].map((field) => `${clean(field.textContent)} :`).join('\n'));
      continue;
    }
    const clone = /** @type {Element} */ (child.cloneNode(true));
    for (const br of clone.querySelectorAll('br')) br.replaceWith('\n');
    const text = (clone.textContent ?? '').split('\n').map(clean).filter(Boolean).join('\n');
    if (text) lines.push(text);
  }
  return lines.join('\n\n');
}

/** @param {string | null} text */
function clean(text) {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

export class LegalContent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    // Children may not be parsed yet when the element connects: enhance once the current task is done.
    queueMicrotask(() => this.addButtons());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  /** Adds a copy button to every template that does not have one yet. */
  addButtons() {
    for (const form of this.querySelectorAll('.lg-form')) {
      if (form.querySelector('.lg-copy')) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'np-go solo lg-copy';
      button.setAttribute('aria-live', 'polite');
      button.innerHTML = `<span></span>${COPY_ICON}`;
      /** @type {HTMLElement} */ (button.firstElementChild).textContent = this.dataset.copy ?? '';
      form.append(button);
    }
  }

  /** @param {MouseEvent} event */
  onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('.lg-copy') : null;
    const form = button?.closest('.lg-form');
    if (!button || !form) return;
    try {
      await navigator.clipboard.writeText(templateText(form));
    } catch {
      return;
    }
    const label = button.querySelector('span') ?? button;
    const initial = label.textContent;
    label.textContent = this.dataset.copied ?? '';
    setTimeout(() => {
      label.textContent = initial;
    }, 2000);
  };
}

if (!customElements.get('legal-content')) customElements.define('legal-content', LegalContent);
